import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { blog, common, components, pages, portfolio, sections, stories } from "../../.velite";
import type { Page, Section } from "../content/schema-types";
import { type SupportedLanguage } from "../utils/url-slug-mapping";

// Re-export the central language type so existing imports from this module keep working
export type { SupportedLanguage };

const DEFAULT_LANGUAGE: SupportedLanguage = "de";
const LANGUAGE_STORAGE_KEY = "quaweb-language";

/**
 * Extract language from URL path
 * Patterns:
 * - Fully localized: /en/... (language prefix at start)
 * - Semi-localized (legacy): /platform/en/..., /produkte/.../en/..., /branchen/en/...
 */
const getLanguageFromPath = (pathname: string): SupportedLanguage | null => {
    // Check for language prefix at the start of the path (fully localized URLs)
    if (pathname.startsWith("/en/") || pathname === "/en") {
        return "en";
    }

    // Check for an /en/ segment anywhere in the path (legacy semi-localized URLs)
    if (pathname.match(/\/en(\/|$)/)) {
        return "en";
    }

    // Default German paths don't have language prefix
    return null;
};

/**
 * Get the initial language from the path alone: a prefixed path is that language,
 * anything else is German.
 *
 * The URL is the only source. localStorage and navigator.language deliberately do
 * NOT participate — they exist on the client but not on the server, so consulting
 * them made the first client render disagree with the server HTML and React threw
 * the whole tree away (hydration mismatch on every page, for every visitor whose
 * browser language did not match the URL).
 *
 * Nothing is lost by ignoring them: the effect below already forces the language
 * back to the URL on every path change, so a stored or browser preference never
 * survived past the first render anyway — it only lived long enough to break
 * hydration.
 */
const getInitialLanguage = (pathname?: string): SupportedLanguage => {
    if (pathname) {
        const urlLang = getLanguageFromPath(pathname);
        if (urlLang) {
            return urlLang;
        }
    }

    return DEFAULT_LANGUAGE;
};

// Import Velite-generated types
type CommonType = (typeof common)[0];
type PagesType = (typeof pages)[0];
type SectionsType = (typeof sections)[0];
type ComponentsType = (typeof components)[0];
type BlogPostsType = (typeof blog)[0];
type StoriesType = (typeof stories)[0];
type PortfolioType = (typeof portfolio)[0];

// Helper types to extract specific types from discriminated unions
// Usage: PageByType<"homepage"> gives Extract<Page, { type: "homepage" }>
// These can be used for type assertions when you know the type:
//   const homepage = content.pages.homepage as PageByType<"homepage">;
export type PageByType<T extends Page["type"]> = Extract<Page, { type: T }>;
export type SectionByType<T extends Section["type"]> = Extract<Section, { type: T }>;
export type ComponentByType<T extends ComponentsType["type"]> = Extract<ComponentsType, { type: T }>;

// ContentModule uses Record<string, UnionType> to leverage discriminated unions
// Type inference works when you narrow by checking the 'type' field:
//   const section = content.sections.benefits;
//   if (section?.type === "benefits") {
//     // TypeScript knows section is Extract<Section, { type: "benefits" }>
//   }
//
// For key-based type inference, use the helper types with type assertions:
//   const homepage: PageByType<"homepage"> = content.pages.homepage as PageByType<"homepage">;
type ContentModule = {
    common: CommonType;
    components: Record<string, ComponentByType<ComponentsType["type"]>>;
    sections: Record<string, SectionByType<Section["type"]>>;
    pages: Record<string, PageByType<Page["type"]>>;
    blog: Record<string, BlogPostsType>;
    stories: Record<string, StoriesType>;
    portfolio: Record<string, PortfolioType>;
};

type LanguageContextType = {
    language: SupportedLanguage;
    setLanguage: (lang: SupportedLanguage) => void;
    content: ContentModule;
    isLoading: boolean;
};

const LanguageContext = createContext<LanguageContextType | null>(null);

export const LanguageProvider: React.FC<{ children: React.ReactNode; initialPath?: string }> = ({
    children,
    initialPath,
}) => {
    const location = useLocation();

    // The one path this provider follows. `initialPath` is an explicit override for
    // tests, which mount the provider without a real router; the app passes nothing
    // and rides on useLocation(). Both the initial state and the effect below read
    // THIS — if they read different sources they contradict each other, and the
    // effect (running last) silently wins.
    const activePath =
        initialPath || location?.pathname || (typeof window !== "undefined" ? window.location.pathname : undefined);

    const [language, setLanguageState] = useState<SupportedLanguage>(() => getInitialLanguage(activePath));

    // The URL is the single source of truth for the active language: a path with
    // no language prefix is German, "/en/..." is English. Deriving the language
    // from the path (rather than mutating it in the click handler) avoids the
    // "second click needed" bug — during navigation there is briefly a render
    // with the new language but the old path, and reconciling against the old
    // path used to reset the language. Following the path only never diverges.
    useEffect(() => {
        const urlLang = getInitialLanguage(activePath);
        if (urlLang !== language) {
            setLanguageState(urlLang);
            if (typeof window !== "undefined") {
                localStorage.setItem(LANGUAGE_STORAGE_KEY, urlLang);
            }
        }
    }, [activePath, language]);

    // Persist language choice to localStorage
    const setLanguage = (lang: SupportedLanguage) => {
        setLanguageState(lang);
        if (typeof window !== "undefined") {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
        }
    };

    // Use useMemo to compute content based on language
    // No async loading needed - Velite data is already bundled
    const content = useMemo(() => {
        // Filter Velite collections by language
        const langPages = pages.filter(p => p.language === language);
        const langSections = sections.filter(s => s.language === language);
        const langComponents = components.filter(c => c.language === language);

        const pagesMap: Record<string, PagesType> = {};
        langPages.forEach(page => {
            const key = page.id.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
            pagesMap[key] = page;
        });
        const sectionsMap: Record<string, SectionsType> = {};
        langSections.forEach(section => {
            const key = section.id.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
            sectionsMap[key] = section;
        });

        const componentsMap: Record<string, ComponentsType> = {};
        langComponents.forEach(comp => {
            const key = comp.id.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
            componentsMap[key] = comp;
        });

        // Filter blog posts by language
        const langBlog = blog.filter(b => b.language === language);

        // Build blog map with camelCase keys
        const blogMap: Record<string, BlogPostsType> = {};
        langBlog.forEach(post => {
            const key = post.id.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
            blogMap[key] = post;
        });

        // Filter and map stories / portfolio by language (same camelCase keying as blog)
        const storiesMap: Record<string, StoriesType> = {};
        stories
            .filter(story => story.language === language)
            .forEach(story => {
                const key = story.id.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
                storiesMap[key] = story;
            });
        const portfolioMap: Record<string, PortfolioType> = {};
        portfolio
            .filter(entry => entry.language === language)
            .forEach(entry => {
                const key = entry.id.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
                portfolioMap[key] = entry;
            });

        // Resolve references in pages (platform.mdx → platform-key-points.mdx links)
        langPages.forEach(page => {
            if (page.references) {
                Object.entries(page.references).forEach(([key, sectionId]) => {
                    const section = langSections.find(s => s.id === sectionId);
                    if (section) {
                        // Attach referenced section data to page
                        // Type assertion needed because we're dynamically adding properties
                        (page as Record<string, unknown>)[key] = section;
                    }
                });
            }
        });

        // Get common content for current language
        const langCommon = common.find(c => c.language === language);

        return {
            common: langCommon!,
            components: componentsMap,
            pages: pagesMap,
            sections: sectionsMap,
            blog: blogMap,
            stories: storiesMap,
            portfolio: portfolioMap,
        };
    }, [language]);

    return (
        <LanguageContext.Provider
            value={{ language, setLanguage, content: content as ContentModule, isLoading: false }}
        >
            {children}
        </LanguageContext.Provider>
    );
};

// SSR-safe fallback content module
const ssrFallbackContent: ContentModule = {
    common: {
        id: "common",
        language: "de",
        cta: {
            learnMore: "Mehr erfahren",
            backToHome: "Zurück zur Startseite",
            viewTerms: "AGB anzeigen →",
            signIn: "Anmelden",
            applyNow: "Jetzt bewerben",
            backToOverviewPrefix: "Zurück zu ",
        },
    },
    components: {},
    sections: {},
    pages: {},
    blog: {},
    stories: {},
    portfolio: {},
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);

    // During SSR, provide safe fallback instead of throwing error
    if (!context) {
        // Only throw in browser environment (indicates actual missing provider)
        if (typeof window !== "undefined") {
            throw new Error("useLanguage must be used within LanguageProvider");
        }

        // Return SSR-safe fallback
        return {
            language: DEFAULT_LANGUAGE,
            setLanguage: () => {},
            content: ssrFallbackContent,
            isLoading: false,
        };
    }

    return context;
};
